import React, { useState, useRef, useLayoutEffect, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

const PLACEMENT = { top: 'top', right: 'right', bottom: 'bottom', left: 'left' };
const GAP = 8;
const Z_INDEX = 999999;

function setRef(ref, value) {
    if (ref == null) return;
    if (typeof ref === 'function') ref(value);
    else ref.current = value;
}

/**
 * Professional tooltip: wraps a single child and shows a styled tooltip on hover/focus.
 * Use instead of native title="" for a consistent app look.
 * @param {React.ReactElement} children - Single element (button, icon, etc.)
 * @param {string} title - Tooltip text
 * @param {string} placement - 'top' | 'right' | 'bottom' | 'left'
 */
export default function Tooltip({ children, title, placement = 'top' }) {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);

    useLayoutEffect(() => {
        if (!visible || !title || !triggerRef.current) {
            setPosition(null);
            return;
        }
        const el = triggerRef.current;
        const rect = el.getBoundingClientRect();
        const padding = 8;
        const tooltipHeight = 32;
        const tooltipWidth = 160;

        let left = 0;
        let top = 0;
        let transform = '';

        switch (placement) {
            case PLACEMENT.top:
                left = rect.left + rect.width / 2;
                top = rect.top - GAP;
                transform = 'translate(-50%, -100%)';
                break;
            case PLACEMENT.bottom:
                left = rect.left + rect.width / 2;
                top = rect.bottom + GAP;
                transform = 'translate(-50%, 0)';
                break;
            case PLACEMENT.left:
                left = rect.left - GAP;
                top = rect.top + rect.height / 2;
                transform = 'translate(-100%, -50%)';
                break;
            case PLACEMENT.right:
            default:
                left = rect.right + GAP;
                top = rect.top + rect.height / 2;
                transform = 'translate(0, -50%)';
                break;
        }

        setPosition({ left, top, transform });
    }, [visible, title, placement]);

    if (!title || !isValidElement(children)) return children;

    const trigger = cloneElement(children, {
        ref: (el) => {
            triggerRef.current = el;
            const orig = children.ref;
            setRef(orig, el);
        },
        onMouseEnter: (e) => {
            children.props?.onMouseEnter?.(e);
            setVisible(true);
        },
        onMouseLeave: (e) => {
            children.props?.onMouseLeave?.(e);
            setVisible(false);
        },
        onFocus: (e) => {
            children.props?.onFocus?.(e);
            setVisible(true);
        },
        onBlur: (e) => {
            children.props?.onBlur?.(e);
            setVisible(false);
        },
    });

    const tooltipEl = visible && position && (
        <div
            className={styles.tooltip}
            role="tooltip"
            style={{
                position: 'fixed',
                left: position.left,
                top: position.top,
                transform: position.transform,
                zIndex: Z_INDEX,
            }}
        >
            {title}
        </div>
    );

    return (
        <>
            {trigger}
            {createPortal(tooltipEl, document.body)}
        </>
    );
}
